import { useState, type ReactNode } from "react";
import type { DesignConditionsView, SurveySummary } from "../api/types.js";
import { UndoNotice } from "./UndoNotice.jsx";

/** What `setDesignConditions` takes, named once rather than derived inline twice. */
type ConditionsInput = Parameters<
  ReturnType<typeof usePortal>["client"]["setDesignConditions"]
>[1];
import { ApiError } from "../api/client.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";

/**
 * The survey's design conditions, changeable (migration 0056): the outdoor
 * design temperature from MIS 3005-D Table 2 — a location and a column, one
 * tap each — or typed; the annual average; the thermal bridging allowance.
 * The heat loss follows each change.
 *
 * The figures the survey already holds are shown at once from the survey
 * itself; the controls arrive with the conditions' own read, and an API that
 * predates it leaves the section as it always was: read only.
 */
export function DesignConditionsCard({
  survey,
  canEdit,
  onChanged,
}: {
  readonly survey: SurveySummary;
  readonly canEdit: boolean;
  readonly onChanged: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { state } = useAsync(async () => client.designConditions(survey.id), [client, survey.id]);
  const [view, setView] = useState<DesignConditionsView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loaded = view ?? (state.status === "ready" ? state.data : null);
  const c = survey.designConditions;
  const temp = loaded?.externalDesignTempC ?? c.externalDesignTempC;
  const column = loaded?.column ?? c.externalDesignTempColumn;
  const annual = loaded?.annualAverageExternalTempC ?? c.annualAverageExternalTempC;
  const bridging = loaded ? loaded.thermalBridgingFactor : c.thermalBridgingFactor;
  const editable = canEdit && loaded !== null;

  /**
   * The state to put back, and the words for what changed.
   *
   * This is the ONE write in the survey that can be truly undone: every field
   * is in the request, and `GET …/design-conditions` hands back the whole
   * prior state. So the previous view is kept and the undo writes it back.
   *
   * `location` and `externalDesignTempC` are COUPLED and the order matters:
   * sending a typed temperature clears the location. A prior state that came
   * from a Table 2 location must therefore be restored as
   * `{ location, column }` and WITHOUT the temperature, or the undo would
   * leave the right number with no location behind it — which reads on a
   * design pack as a figure somebody typed.
   */
  const [undo, setUndo] = useState<{ input: ConditionsInput; what: string } | null>(null);

  const reversalOf = (before: DesignConditionsView): ConditionsInput =>
    before.location !== null
      ? {
          location: before.location,
          column: before.column,
          annualAverageExternalTempC: before.annualAverageExternalTempC,
          thermalBridgingFactor: before.thermalBridgingFactor,
          ...(before.countHeatBetweenRooms === undefined
            ? {}
            : { countHeatBetweenRooms: before.countHeatBetweenRooms }),
        }
      : {
          externalDesignTempC: before.externalDesignTempC,
          annualAverageExternalTempC: before.annualAverageExternalTempC,
          thermalBridgingFactor: before.thermalBridgingFactor,
          ...(before.countHeatBetweenRooms === undefined
            ? {}
            : { countHeatBetweenRooms: before.countHeatBetweenRooms }),
        };

  const describe = (before: DesignConditionsView, after: DesignConditionsView): string => {
    if (before.externalDesignTempC !== after.externalDesignTempC) {
      return `The outdoor design temperature is now ${after.externalDesignTempC.toFixed(1)} °C.`;
    }
    if (before.countHeatBetweenRooms !== after.countHeatBetweenRooms) {
      return after.countHeatBetweenRooms === true
        ? "Heat between heated rooms is now counted."
        : "Internal walls, doors and windows are now taken at U = 0.";
    }
    if (before.thermalBridgingFactor !== after.thermalBridgingFactor) {
      return after.thermalBridgingFactor === null
        ? "Thermal bridging is back to the engine's default."
        : `Thermal bridging is now +${Math.round(after.thermalBridgingFactor * 100)}%.`;
    }
    return "The design conditions were changed.";
  };

  const change = (
    input: Parameters<typeof client.setDesignConditions>[1],
    opts: { readonly reversing?: boolean } = {},
  ): void => {
    const before = loaded;
    setBusy(true);
    setError(null);
    client
      .setDesignConditions(survey.id, input)
      .then((next) => {
        setView(next);
        // An undo offers no undo of its own: two of them in a row is a toggle
        // nobody asked for, and the card already shows the figure.
        setUndo(
          opts.reversing || before === null
            ? null
            : { input: reversalOf(before), what: describe(before, next) },
        );
        onChanged();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "The change could not be saved."))
      .finally(() => setBusy(false));
  };

  return (
    <section className="design-conditions" data-testid="design-conditions">
      <h2>Design conditions</h2>

      {/* A true Undo: every field is in the request and the whole prior state
          goes back. No caveat, because nothing stays behind. */}
      {undo !== null ? (
        <UndoNotice
          what={undo.what}
          actionLabel="Undo"
          busy={busy}
          onAction={() => change(undo.input, { reversing: true })}
          onDismiss={() => setUndo(null)}
        />
      ) : null}
      <div className="design-conditions__summary">
        <div>
          <span className="design-conditions__figure">{temp.toFixed(1)} °C</span>
          <span className="muted small">
            {" "}
            outdoor design ·{" "}
            {loaded?.location ? `${loaded.location}, ` : ""}
            {column === "B" ? "99.6% of hours (column B)" : "99% of hours (column A)"}
          </span>
        </div>
        <div className="muted small">
          Annual average {annual.toFixed(1)} °C · thermal bridging{" "}
          {bridging === null
            ? "not set"
            : bridging === 0
              ? "none"
              : `+${Math.round(bridging * 100)}%`}
          {" · "}
          {loaded?.countHeatBetweenRooms ? "heat between heated rooms counted" : "internal walls, doors and windows U = 0"}
          {loaded?.setBy ? ` · changed by ${loaded.setBy.name}` : ""}
        </div>
      </div>

      {editable && loaded ? (
        <div className="design-conditions__controls" aria-busy={busy}>
          <div className="design-conditions__row">
            <span className="design-conditions__label">Nearest location (MIS 3005-D Table 2)</span>
            <div className="site-survey__chips" role="group" aria-label="Nearest location">
              {loaded.table.map((row) => (
                <button
                  key={row.location}
                  type="button"
                  className="site-survey__chip"
                  aria-pressed={loaded.location === row.location}
                  disabled={busy}
                  title={`${row.altitudeM} m · ${row.columnA} °C (99%) · ${row.columnB} °C (99.6%)`}
                  onClick={() => change({ location: row.location, column })}
                >
                  {row.location}
                </button>
              ))}
            </div>
          </div>
          <div className="design-conditions__row">
            <span className="design-conditions__label">Design for</span>
            <div className="site-survey__chips" role="group" aria-label="Design for">
              <button type="button" className="site-survey__chip" aria-pressed={column === "B"} disabled={busy} onClick={() => change({ column: "B" })}>
                99.6% of hours
              </button>
              <button type="button" className="site-survey__chip" aria-pressed={column === "A"} disabled={busy} onClick={() => change({ column: "A" })}>
                99% of hours
              </button>
            </div>
            <span className="site-survey__help">
              {column === "A"
                ? "Column A needs an intermittent-heating uplift (MIS 3005-D §3.4.1(a)); column B does not."
                : "Column B needs no intermittent-heating uplift (MIS 3005-D §3.4.1(a))."}
              {loaded.location ? ` ${loaded.location} is tabulated at ${loaded.table.find((r) => r.location === loaded.location)?.altitudeM} m — a site much higher is colder: type the temperature.` : ""}
            </span>
          </div>
          <div className="design-conditions__inputs">
            <NumberField
              label="Outdoor design temperature"
              unit="°C"
              value={temp}
              disabled={busy}
              onCommit={(v) => change({ externalDesignTempC: v })}
            />
            <NumberField
              label="Annual average"
              unit="°C"
              value={annual}
              disabled={busy}
              onCommit={(v) => change({ annualAverageExternalTempC: v })}
            />
            <div className="design-conditions__row">
              <span className="design-conditions__label">Thermal bridging</span>
              <div className="site-survey__chips" role="group" aria-label="Thermal bridging">
                {[
                  { label: "None", value: 0 },
                  { label: "+10%", value: 0.1 },
                  { label: "+15%", value: 0.15 },
                  { label: "+25%", value: 0.25 },
                ].map((o) => (
                  <button
                    key={o.label}
                    type="button"
                    className="site-survey__chip"
                    aria-pressed={bridging !== null && Math.abs(bridging - o.value) < 1e-6}
                    disabled={busy}
                    onClick={() => change({ thermalBridgingFactor: o.value })}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="design-conditions__row">
              <span className="design-conditions__label">Internal walls and doors</span>
              <div className="site-survey__chips" role="group" aria-label="Internal walls and doors">
                <button
                  type="button"
                  className="site-survey__chip"
                  aria-pressed={loaded.countHeatBetweenRooms !== true}
                  disabled={busy}
                  onClick={() => change({ countHeatBetweenRooms: false })}
                >
                  U = 0 (not counted)
                </button>
                <button
                  type="button"
                  className="site-survey__chip"
                  aria-pressed={loaded.countHeatBetweenRooms === true}
                  disabled={busy}
                  onClick={() => change({ countHeatBetweenRooms: true })}
                >
                  Count heat between rooms
                </button>
              </div>
              <span className="site-survey__help">
                {loaded.countHeatBetweenRooms
                  ? "Heat passing from a warmer heated room to a cooler one, through internal walls, doors and windows, is counted at their U-values."
                  : "Internal walls, doors and windows between heated rooms are taken at U = 0: no heat passes between rooms. Walls and doors to an unheated space — a communal stair, a garage, a loft — are still counted. A party element is always taken at U = 0, whichever way this is set: the other side is another home."}
              </span>
            </div>
          </div>
        </div>
      ) : null}
      {error ? (
        <p className="notice error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function NumberField({
  label,
  unit,
  value,
  disabled,
  onCommit,
}: {
  readonly label: string;
  readonly unit: string;
  readonly value: number;
  readonly disabled: boolean;
  readonly onCommit: (value: number) => void;
}): ReactNode {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? value.toFixed(1);
  const commit = (): void => {
    if (draft === null) return;
    const n = Number(draft.replace(",", "."));
    setDraft(null);
    if (Number.isFinite(n) && Math.abs(n - value) > 1e-9) onCommit(n);
  };
  return (
    <label className="design-conditions__row">
      <span className="design-conditions__label">{label}</span>
      <span className="site-survey__number">
        <input
          type="text"
          inputMode="decimal"
          value={shown}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
        />
        <span className="muted">{unit}</span>
      </span>
    </label>
  );
}
